import React from "react";
import { useNavigate } from "react-router-dom";
import { X, Check, Sparkles, Zap } from "lucide-react";

/**
 * Universal upgrade modal (v2, 3-tier).
 * Opens whenever any part of the app calls plan.showUpgrade(reasonKey).
 */
const PLAN_CARDS = {
  premium: {
    name: "Premium",
    price: "$20",
    regular: "$40",
    accent: "#7aa2ff",
    tagline: "Unlimited watchlists, SMS alerts & AI Assistant",
    perks: [
      "Unlimited watchlists (30 symbols each)",
      "Full live scanner + real-time market data",
      "SMS Alerts via Twilio",
      "AI Trading Assistant",
      "Custom filters + full indicator library",
      "No FlowFinder branding",
    ],
  },
  executive: {
    name: "Executive",
    price: "$50",
    regular: "$100",
    accent: "#ffd166",
    tagline: "Institutional-grade research + backtesting",
    perks: [
      "Everything in Premium",
      "Professional Backtester (unlimited)",
      "Unlimited watchlists (1,000 symbols each)",
      "Saved strategies + comparison",
      "Trade Replay",
      "CSV / Excel / PDF exports",
      "Executive badge + priority support",
    ],
  },
};

export default function UpgradeModal({ open, reason, onClose }) {
  const navigate = useNavigate();
  if (!open || !reason) return null;

  const suggested = PLAN_CARDS[reason.suggested] || PLAN_CARDS.premium;

  const go = () => {
    onClose();
    navigate("/pricing");
  };

  return (
    <div
      className="fixed inset-0 z-[95] bg-black/75 backdrop-blur-2xl flex items-center justify-center p-4 fade-in"
      onClick={onClose}
      data-testid="upgrade-modal"
    >
      <button
        onClick={onClose}
        aria-label="Close upgrade modal"
        className="absolute top-5 right-5 text-white/50 hover:text-white p-2"
        data-testid="upgrade-modal-close"
      >
        <X className="w-5 h-5" />
      </button>
      <div
        onClick={(e) => e.stopPropagation()}
        className="card-surface w-full max-w-xl p-6 sm:p-7 relative overflow-hidden"
      >
        <div
          className="absolute inset-0 pointer-events-none opacity-60"
          style={{ background: `radial-gradient(circle at 100% 0%, ${suggested.accent}22, transparent 60%)` }}
        />
        <div className="relative">
          <div className="flex items-center gap-2 mb-1">
            <Sparkles className="w-4 h-4" style={{ color: suggested.accent }} />
            <div className="label-mono">Upgrade</div>
          </div>
          <h2 className="text-2xl sm:text-[26px] text-white font-semibold tracking-tight leading-tight" data-testid="upgrade-title">
            {reason.title}
          </h2>
          <p className="text-white/65 text-sm mt-2 leading-relaxed" data-testid="upgrade-body">
            {reason.body}
          </p>

          <div
            className="mt-5 rounded-2xl border p-4"
            style={{ borderColor: `${suggested.accent}44`, background: `${suggested.accent}0d` }}
            data-testid={`upgrade-plan-${reason.suggested}`}
          >
            <div className="flex items-baseline justify-between">
              <div>
                <div className="text-white text-[15px] font-semibold tracking-tight">{suggested.name}</div>
                <div className="text-white/55 text-[12px]">{suggested.tagline}</div>
              </div>
              <div className="text-right">
                <div className="text-white font-mono text-xl tabular-nums">
                  {suggested.price}
                  <span className="text-white/40 text-[11px] ml-1">/mo</span>
                </div>
                {suggested.regular && (
                  <div className="text-white/40 text-[11px] font-mono line-through">{suggested.regular}/mo</div>
                )}
              </div>
            </div>
            <ul className="mt-3 space-y-1.5 text-[13px] text-white/80">
              {suggested.perks.map((p, i) => (
                <li key={i} className="flex items-center gap-2">
                  <Check className="w-3.5 h-3.5" style={{ color: suggested.accent }} />
                  {p}
                </li>
              ))}
            </ul>
          </div>

          <div className="mt-5 flex items-center gap-2">
            <button
              onClick={go}
              className="btn-accent py-2.5 px-5 inline-flex items-center gap-2"
              data-testid="upgrade-cta"
            >
              <Zap className="w-3.5 h-3.5" />
              Upgrade Now
            </button>
            <button onClick={onClose} className="btn-ghost py-2.5 px-4" data-testid="upgrade-continue-free">
              Continue Free
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
